import {
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  IconButtonSection,
  IconSizeEnum,
  ModalDialog,
  ModalMessage,
  ModalMessageColorsEnum,
  PlusIcon,
  Typography,
} from '@acceso/design-system';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { TableWithDetailCard } from '@/components/TableWithDetailCard/TableWithDetailCard';
import { FormTypeEnum } from '@/types';
import { getParamsFromTableFilters } from '@/utils/table-filters';

import { useFeaturedPeople } from '../api/get-featured-people';
import { usePatchFeaturedPeople } from '../api/patch-featured-people';
import { usePostFeaturedPeople } from '../api/post-featured-people';
import { FeaturedPeopleDetailCard } from '../components/FeaturedPeopleDetailCard';
import { FeaturedPeopleDrawerForm } from '../components/FeaturedPeopleDrawerForm';
import { FeaturedPeopleTable } from '../components/FeaturedPeopleTable';
import { useFeaturedPeopleControllers } from '../hooks/useFeaturedPeopleControllers';
import { useFeaturedPeoplePermissions } from '../hooks/useFeaturedPeoplePermissions';
import { useFeaturedPeopleFilters } from '../stores/featured-people-filters';
import { TFeaturedPeopleType } from '../types';

export default function FeaturedPeoplePage() {
  const { t } = useTranslation();

  const { filters, paginationValues, clearFilters } = useFeaturedPeopleFilters();

  /**
   * Resets table filters on page unmount.
   */
  useEffect(() => {
    return () => {
      clearFilters();
    };
  }, [clearFilters]);

  const {
    data: { data: featuredPeople = [], total: totalItems = 0 } = {},
    isFetching,
    isError,
  } = useFeaturedPeople({
    params: getParamsFromTableFilters({
      filters,
      paginationValues,
    }),
  });

  const {
    featuredPeopleDrawer,
    featuredPeopleDialog,
    handleDeleteFeaturedPeople,
    multiFeaturedPeopleDialog,
    handleDeleteMultipleFeaturedPeople,
  } = useFeaturedPeopleControllers();

  const { mutate: createFeaturedPeople, isPending: isCreatingFeaturedPeople } =
    usePostFeaturedPeople();

  const { mutate: updateFeaturedPeople, isPending: isUpdatingFeaturedPeople } =
    usePatchFeaturedPeople();

  const { canCreate, canUpdate, canDuplicate, canDelete } = useFeaturedPeoplePermissions();

  const onCreate = () => {
    if (!canCreate) return;
    featuredPeopleDrawer.open({ type: FormTypeEnum.CREATE, data: null });
  };

  const onUpdate = (featuredPerson: TFeaturedPeopleType) => {
    if (!canUpdate) return;
    featuredPeopleDrawer.open({ type: FormTypeEnum.UPDATE, data: featuredPerson });
  };

  const onDuplicate = (featuredPerson: TFeaturedPeopleType) => {
    if (!canDuplicate) return;
    featuredPeopleDrawer.open({ type: FormTypeEnum.DUPLICATE, data: featuredPerson });
  };

  const onDelete = (featuredPerson: TFeaturedPeopleType) => {
    if (!canDelete) return;
    featuredPeopleDialog.open(featuredPerson);
  };

  const onMultiDelete = (peoples: TFeaturedPeopleType[]) => {
    if (!canDelete) return;
    multiFeaturedPeopleDialog.open(peoples);
  };

  return (
    <>
      <div className="flex flex-col">
        <div className="flex justify-between py-size-8">
          <Typography
            fontSize={FontSizeEnum.Xl}
            weight={FontWeightEnum.Bold}
            color={FontColorEnum.Dark10}
          >
            {t('Personas de interés')}
          </Typography>

          {canCreate && (
            <IconButtonSection
              className="mr-size-16"
              onClick={onCreate}
              internalProps={{
                icon: {
                  size: IconSizeEnum.SM,
                  type: PlusIcon,
                },
                text: {
                  value: t('Nuevo'),
                  color: FontColorEnum.Default,
                },
              }}
            />
          )}
        </div>

        <TableWithDetailCard<TFeaturedPeopleType>
          isError={isError}
          renderTable={({ handleOpenSecondCard, data }) => (
            <FeaturedPeopleTable
              items={featuredPeople}
              totalItems={totalItems}
              isFetching={isFetching}
              activeItem={data}
              onUpdate={canUpdate ? onUpdate : undefined}
              onDuplicate={canDuplicate ? onDuplicate : undefined}
              onDelete={canDelete ? onDelete : undefined}
              onMultiDelete={canDelete ? onMultiDelete : undefined}
              onSelect={handleOpenSecondCard}
            />
          )}
          renderDetailCard={({ data, isSecondCardCollapsed, handleSecondCardCollapse }) => (
            <FeaturedPeopleDetailCard
              featuredPeople={data}
              isCollapsed={isSecondCardCollapsed}
              onCollapse={handleSecondCardCollapse}
              onUpdate={onUpdate}
            />
          )}
        />
      </div>

      <FeaturedPeopleDrawerForm
        isOpen={featuredPeopleDrawer.isOpen}
        onClose={featuredPeopleDrawer.close}
        onSubmit={data => {
          if (featuredPeopleDrawer.data?.type === FormTypeEnum.UPDATE) {
            updateFeaturedPeople(
              {
                ...data,
                parameters: {
                  logos: data.logos_presigned_urls,
                  main_logo: typeof data.main_logo === 'string' ? data.main_logo : undefined,
                },
                id: featuredPeopleDrawer.data.data.id,
                main_logo: data.main_logo instanceof File ? data.main_logo : undefined,
              },
              {
                onSuccess: () => {
                  featuredPeopleDrawer.close();
                },
              }
            );
          } else {
            createFeaturedPeople(data, {
              onSuccess: () => {
                featuredPeopleDrawer.close();
              },
            });
          }
        }}
        isMutating={isCreatingFeaturedPeople || isUpdatingFeaturedPeople}
        data={featuredPeopleDrawer.data || undefined}
      />

      <ModalDialog
        isOpen={featuredPeopleDialog.isOpen}
        onClose={featuredPeopleDialog.close}
        onAction={handleDeleteFeaturedPeople}
        heading={t('Eliminar personas de interés')}
        actionLabel={t('Eliminar')}
        linkLabel={t('Cancelar')}
        color={ModalMessageColorsEnum.ERROR}
      >
        <ModalMessage
          title={t('¿Estás seguro de querer eliminar a la personas de interés seleccionado?')}
        />
      </ModalDialog>

      <ModalDialog
        isOpen={multiFeaturedPeopleDialog.isOpen}
        onClose={multiFeaturedPeopleDialog.close}
        onAction={handleDeleteMultipleFeaturedPeople}
        heading={t('Eliminar personas de interés')}
        actionLabel={t('Eliminar')}
        linkLabel={t('Cancelar')}
        color={ModalMessageColorsEnum.ERROR}
      >
        <ModalMessage
          title={t('¿Estás seguro de querer eliminar las personas de interés seleccionadas?')}
        />
      </ModalDialog>
    </>
  );
}
